import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import FormattedNumber from "@/shared/components/FormattedNumber";
import {
  Card,
  FlexRow,
  TableWrap,
  Table,
  Th,
  Td,
  SmallTable,
  SmallTh,
  SmallTd,
  GhostButton,
  BackButton,
  NetworkBadge,
  BadgeDir,
  TreemapBar,
  TreemapSegment,
  TreemapLabel,
  SidebarOverlay,
  SidebarGlass,
} from "./styledComponents";
import {
  getStrigaWalletAddress,
  initiateIntraledgerTransaction,
  initiateSepaTransaction,
  initiateOnChainTransaction,
  confirmOTP,
  getFeeEstimateStriga,
} from "@/utils/services/user.service";
import config from "@/config/walletConnect";
import { API_URL } from "@/utils/services/user.service";
import authHeader from "@/utils/services/auth-header";

function BankWalletBlock(props) {
  const { 
    items, 
    onWalletDetails, 
    onRefresh, 
    pendingBalance, 
    onRefreshUpcoming,
    showAddressView,
    addresses,
    onBack,
    activity,
    movements,
    onRowClick,
    onBackFromActivity,
    isLoadingActivities,
  } = props;

  // Get network constants
  const {
    REACT_APP_SOLANA,
    REACT_APP_TRON,
  } = config;
  const totalForTreemap = items.reduce((sum, a) => sum + (a.balance <= 0 ? 0 : a.balance), 0) || 1;

  // Sidebar states
  const [sidebarWithdraw, setSidebarWithdraw] = useState(null);
  const [infoToken, setInfoToken] = useState(null);

  // Bank withdraw states
  const [selectedBankWalletFrom, setSelectedBankWalletFrom] = useState("intraledger_payment");
  const [selectedBankWalletTo, setSelectedBankWalletTo] = useState("virtual");
  const [bankWithdrawAmount, setBankWithdrawAmount] = useState("");
  const [bankWithdrawCoin, setBankWithdrawCoin] = useState(null);
  const [bankWithdrawOptions, setBankWithdrawOptions] = useState([]);
  const [bankWithdrawReceiver, setBankWithdrawReceiver] = useState(null);
  const [bankApproverData, setBankApproverData] = useState([]);
  const [searchLoad, setSearchLoad] = useState(false);
  const [strigaWalletAddress, setStrigaWalletAddress] = useState(null);
  const [strigaWalletDetail, setStrigaWalletDetail] = useState(null);
  const [defaultBenificiary, setDefaultBenificiary] = useState(null);
  const [challengeId, setChallengeId] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [bic, setBic] = useState("");
  const [iban, setIban] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [onChainWalletAddress, setOnChainWalletAddress] = useState("");
  const [onchainFeeEstimation, setOnchainFeeEstimation] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [approxAmountAfterFee, setApproxAmountAfterFee] = useState(0);

  // Get wallet details
  useEffect(() => {
    const getWalletDetail = async () => {
      setIsLoading(false);
      // getAllWallets removed
    };
    getWalletDetail();
  }, []);

  // Get Striga wallet address
  useEffect(() => {
    const getStrigaWallet = async (id) => {
      if (!id) return;
      setIsLoading(true);
      try {
        const payload = { striga_user_id: id };
        const res = await getStrigaWalletAddress(payload);
        setStrigaWalletAddress(res?.data);
      } catch (error) {
        console.error("Error fetching Striga wallet:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getStrigaWallet(defaultBenificiary);
  }, [selectedBankWalletFrom, defaultBenificiary]);

  // Update coin options based on wallet detail
  useEffect(() => {
    if (strigaWalletDetail && strigaWalletDetail.wallets) {
      const firstWallet = strigaWalletDetail.wallets[0];
      if (firstWallet && firstWallet.accounts) {
        const accounts = Object.values(firstWallet.accounts);
        const chainIdMapping = {
          BNB: '97',
          USDT: '11155111',
          USDC: '11155111',
          ETH: '11155111'
        };

        const options = accounts
          .filter(account => {
            if (selectedBankWalletFrom === "sepa_payment") {
              return account.currency === "EUR";
            }
            if (selectedBankWalletTo === "bank") {
              return account.currency !== "EUR" && account.currency !== "BTC";
            }
            if (selectedBankWalletFrom === "onChain_Transection") {
              return account.currency !== "EUR";
            }
            return true;
          })
          .map(account => ({
            value: account.currency,
            label: account.currency,
            accountId: account.accountId,
            availableBalance: account.availableBalance?.hAmount || account.availableBalance?.amount,
            chainId: chainIdMapping[account.currency],
            hAmount: parseFloat(account.availableBalance?.hAmount || account.availableBalance?.amount || 0),
          }));

        setBankWithdrawOptions(options);
      }
    }
  }, [strigaWalletDetail, selectedBankWalletFrom, selectedBankWalletTo]);

  // Handle recipient search
  const handleBankRecipientSearch = async (name) => {
    if (name.length < 2) {
      setBankApproverData([]);
      return;
    }

    setSearchLoad(true);
    try {
      const res = await axios.get(`${API_URL}users?name=${encodeURIComponent(name)}`, { headers: authHeader() });
      if (!res.data?.user) {
        setBankApproverData([]);
        setSearchLoad(false);
        return;
      }
      const arr = res.data.user.map((i) => ({
        id: i._id,
        address: i.address,
        value: i.name,
        label: i.name,
        sol_address: i?.sol_address,
        btcAddress: i?.btcAddress,
        destinationUserId: i?.striga_user_id,
        onChainAddress: i?.secondary_wallet_address,
        onChainSolAddress: i?.secondary_wallet_sol_address,
        trcWalletAddress: i?.trcWalletAddress,
        striga_user_id: i?.striga_user_id
      }));

      const matchingOptions = arr.filter(
        (option) => option.value.toLowerCase() === name.toLowerCase()
      );
      
      if (matchingOptions.length > 0) {
        setDefaultBenificiary(matchingOptions[0]?.destinationUserId);
        setBankWithdrawReceiver(matchingOptions[0]);
      }
      
      setBankApproverData(matchingOptions);
    } catch (error) {
      console.error("Error searching recipient:", error);
      setBankApproverData([]);
    } finally {
      setSearchLoad(false);
    }
  };

  // Auto-select coin when sidebar opens and options are available
  useEffect(() => {
    if (sidebarWithdraw && bankWithdrawOptions.length > 0 && !bankWithdrawCoin) {
      const currency = sidebarWithdraw.currency;
      const matchingCoin = bankWithdrawOptions.find(opt => opt.value === currency);
      if (matchingCoin) {
        setBankWithdrawCoin(matchingCoin);
      }
    }
  }, [sidebarWithdraw, bankWithdrawOptions, bankWithdrawCoin]);

  // Update address when "To" or recipient changes - based on network like OnchainSendFormTemp
  useEffect(() => {
    if (selectedBankWalletFrom === "onChain_Transection" && bankWithdrawReceiver) {
      if (selectedBankWalletTo === "virtual") {
        // For virtual wallet, use blockchain deposit address from Striga
        if (strigaWalletAddress?.blockchainDepositAddress) {
          setOnChainWalletAddress(strigaWalletAddress.blockchainDepositAddress);
        } else if (bankWithdrawReceiver.btcAddress && bankWithdrawCoin?.value === "BTC") {
          setOnChainWalletAddress(bankWithdrawReceiver.btcAddress);
        } else {
          setOnChainWalletAddress(bankWithdrawReceiver.address || "");
        }
      } else if (selectedBankWalletTo === "onChain") {
        // For onchain wallet, use network-specific address based on coin's chainId
        const chainId = bankWithdrawCoin?.chainId;
        
        if (chainId === REACT_APP_SOLANA) {
          // For SOLANA, use onChainSolAddress or sol_address
          setOnChainWalletAddress(
            bankWithdrawReceiver.onChainSolAddress || 
            bankWithdrawReceiver.sol_address || 
            ""
          );
        } else if (chainId === REACT_APP_TRON) {
          // For TRON, use TRC address
          setOnChainWalletAddress(
            bankWithdrawReceiver.trcWalletAddress || 
            bankWithdrawReceiver.address || 
            ""
          );
        } else {
          // For other networks (BSC, Polygon, ETH), use onChainAddress
          setOnChainWalletAddress(
            bankWithdrawReceiver.onChainAddress || 
            bankWithdrawReceiver.secondary_wallet_address || 
            ""
          );
        }
      } else if (selectedBankWalletTo === "external_wallet") {
        // For external, keep manual input
        setOnChainWalletAddress(onChainWalletAddress || "");
      }
    }
  }, [selectedBankWalletTo, bankWithdrawReceiver, strigaWalletAddress, bankWithdrawCoin, selectedBankWalletFrom, REACT_APP_SOLANA, REACT_APP_TRON]);

  // Fetch fee for onChain transactions
  useEffect(() => {
    const fetchFee = async () => {
      if (selectedBankWalletFrom === "onChain_Transection" && bankWithdrawAmount && bankWithdrawCoin && onChainWalletAddress) {
        setIsLoadingFee(true);
        try {
          const payload = {
            sourceAccountId: bankWithdrawCoin.accountId,
            walletAddress: onChainWalletAddress,
            amount: bankWithdrawAmount,
            symbol: bankWithdrawCoin.value
          };
          const res = await getFeeEstimateStriga(payload);
          setOnchainFeeEstimation(res?.data);
          if (res?.data?.HAmountTotalFee) {
            const fee = parseFloat(res.data.HAmountTotalFee);
            const amount = parseFloat(bankWithdrawAmount);
            setApproxAmountAfterFee(amount - fee);
          }
        } catch (error) {
          console.error("Error fetching fee:", error);
          setOnchainFeeEstimation(null);
          setApproxAmountAfterFee(0);
        } finally {
          setIsLoadingFee(false);
        }
      } else {
        setOnchainFeeEstimation(null);
        setApproxAmountAfterFee(0);
      }
    };
    fetchFee();
  }, [selectedBankWalletFrom, bankWithdrawAmount, bankWithdrawCoin, onChainWalletAddress]);

  // Auto-fill recipient when "To" changes to virtual
  useEffect(() => {
    if (selectedBankWalletTo === "virtual" && selectedBankWalletFrom === "onChain_Transection" && !bankWithdrawReceiver) {
      const userDataInfo = JSON.parse(localStorage.getItem("user"));
      if (userDataInfo) {
        const defaultUser = `${userDataInfo?.data?.user?.firstName} ${userDataInfo?.data?.user?.lastName}`;
        if (defaultUser && defaultUser.trim().length >= 2) {
          handleBankRecipientSearch(defaultUser);
        }
      }
    }
  }, [selectedBankWalletTo, selectedBankWalletFrom]);

  // Reset when sidebar closes
  useEffect(() => {
    if (!sidebarWithdraw) {
      setBankWithdrawCoin(null);
      setBankWithdrawAmount("");
      setBankWithdrawReceiver(null);
      setChallengeId("");
      setOtpCode("");
      setSelectedBankWalletFrom("intraledger_payment");
      setSelectedBankWalletTo("virtual");
      setBankApproverData([]);
      setOnChainWalletAddress("");
      setBic("");
      setIban("");
    }
  }, [sidebarWithdraw]);

  // Handle submit
  const handleBankWithdrawSubmit = async () => {
    if (!bankWithdrawAmount || !bankWithdrawCoin) {
      toast.error("Please fill all required fields");
      return;
    }
    
    // Validation
    if (selectedBankWalletFrom === "intraledger_payment" && !bankWithdrawReceiver?.destinationUserId) {
      toast.error("Please select a recipient");
      return;
    }
    if (selectedBankWalletFrom === "sepa_payment" && (!iban || !bic) && (!strigaWalletAddress?.IBAN || !strigaWalletAddress?.BIC)) {
      toast.error("Please enter BIC and IBAN");
      return;
    }
    if (selectedBankWalletFrom === "onChain_Transection" && !onChainWalletAddress) {
      toast.error("Please enter recipient address");
      return;
    }

    setIsLoading(true);
    try {
      if (selectedBankWalletFrom === "intraledger_payment") {
        const payload = {
          sourceAccountId: bankWithdrawCoin.accountId,
          amount: bankWithdrawAmount,
          destinationSymbol: bankWithdrawCoin.value,
          destinationUserId: bankWithdrawReceiver?.destinationUserId
        };
        const res = await initiateIntraledgerTransaction(payload);
        setChallengeId(res?.data?.challengeId);
        toast.success("OTP sent to your email");
      } else if (selectedBankWalletFrom === "sepa_payment") {
        const payload = {
          iban: iban || strigaWalletAddress?.IBAN,
          bic: bic || strigaWalletAddress?.BIC,
          sourceAccountId: bankWithdrawCoin.accountId,
          amount: bankWithdrawAmount
        };
        if (!payload.iban || !payload.bic) {
          toast.error("Please enter BIC and IBAN");
          return;
        }
        const res = await initiateSepaTransaction(payload);
        setChallengeId(res?.data?.challengeId);
        toast.success("OTP sent to your email");
      } else if (selectedBankWalletFrom === "onChain_Transection") {
        if (!onChainWalletAddress) {
          toast.error("Recipient address is required");
          return;
        }
        const payload = {
          sourceAccountId: bankWithdrawCoin.accountId,
          walletAddress: onChainWalletAddress,
          amount: bankWithdrawAmount,
          symbol: bankWithdrawCoin.value
        };
        // Fee should already be calculated in useEffect
        if (onchainFeeEstimation?.HAmountTotalFee) {
          const totalNeeded = parseFloat(bankWithdrawAmount) + parseFloat(onchainFeeEstimation.HAmountTotalFee);
          if (totalNeeded > parseFloat(bankWithdrawCoin.availableBalance || bankWithdrawCoin.hAmount || 0)) {
            toast.error("You don't have enough available balance");
            return;
          }
        }
        const res = await initiateOnChainTransaction(payload);
        if (res?.data?.challengeId) {
          setChallengeId(res.data.challengeId);
          toast.success("OTP sent to your email");
        } else {
          toast.success("Transaction initiated");
          setSidebarWithdraw(null);
          setBankWithdrawAmount("");
          setBankWithdrawCoin(null);
          setBankWithdrawReceiver(null);
          setChallengeId("");
          setOtpCode("");
          setOnChainWalletAddress("");
          if (onRefresh) onRefresh();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.Message || "Transaction failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification
  const handleBankOTPVerify = async () => {
    if (!otpCode || !challengeId) {
      toast.error("Please enter OTP code");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        challengeId: challengeId,
        verificationCode: otpCode,
        amount: bankWithdrawAmount,
        symbol: bankWithdrawCoin.value,
        reciever: selectedBankWalletFrom === "intraledger_payment" 
          ? bankWithdrawReceiver?.destinationUserId 
          : selectedBankWalletFrom === "sepa_payment" 
          ? strigaWalletAddress?.IBAN 
          : strigaWalletAddress?.blockchainDepositAddress
      };
      const res = await confirmOTP(payload);
      if (res) {
        toast.success("Transaction Successful");
        setSidebarWithdraw(null);
        setBankWithdrawAmount("");
        setBankWithdrawCoin(null);
        setBankWithdrawReceiver(null);
        setChallengeId("");
        setOtpCode("");
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "OTP verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Card>
      <FlexRow gap={8} style={{ justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ marginBottom: 4 }}>Bank Wallet</h3>
          <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 0 }}>
            Conti bancari collegati in EUR / USD / altre valute, per on/off-ramp.
          </p>
        </div>
        <FlexRow gap={6}>
          <GhostButton onClick={onWalletDetails}>Wallet details</GhostButton>
        </FlexRow>
      </FlexRow>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Currency</Th>
              <Th>Quantity</Th>
              <Th>Balance</Th>
              <Th>Upcoming</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const pendingCoin = pendingBalance
                ? pendingBalance.find((coin) => coin.symbol === a.currency || coin.currency === a.currency)
                : null;
              const isActive = activity && activity.wallet === "Bank" && activity.symbol === a.currency;
              return (
                <tr 
                  key={`bank-${a.currency}`}
                  onClick={() => onRowClick && onRowClick(a.currency)}
                  style={{ 
                    cursor: onRowClick ? "pointer" : "default",
                    backgroundColor: isActive ? "rgba(0, 247, 210, 0.1)" : "transparent",
                    transition: "background-color 0.2s ease"
                  }}
                >
                  <Td>{a.currency}</Td>
                  <Td>
                    <FormattedNumber value={a.qty || 0} maxDecimals={2} />
                  </Td>
                  <Td>
                    <FormattedNumber value={a.balance || 0} symbol="$" maxDecimals={2} />
                  </Td>
                  <Td>
                    {pendingCoin && pendingCoin.quantity ? (
                      <FormattedNumber value={parseFloat(pendingCoin.quantity)} maxDecimals={8} />
                    ) : (
                      "--"
                    )}
                  </Td>
                  <Td>
                    <FlexRow gap={6}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toast.info("Deposit functionality coming soon");
                        }}
                        style={{
                          background: "none",
                          border: "1px solid #5ce9ff",
                          color: "#5ce9ff",
                          borderRadius: "8px",
                          padding: "4px 8px",
                          cursor: "pointer",
                          fontSize: "11px",
                        }}
                      >
                        Deposit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSidebarWithdraw(a);
                        }}
                        style={{
                          background: "none",
                          border: "1px solid #5ce9ff",
                          color: "#5ce9ff",
                          borderRadius: "8px",
                          padding: "4px 8px",
                          cursor: "pointer",
                          fontSize: "11px",
                        }}
                      >
                        Withdraw
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoToken(a);
                        }}
                        style={{
                          background: "none",
                          border: "1px solid #5ce9ff",
                          color: "#5ce9ff",
                          borderRadius: "8px",
                          padding: "4px 8px",
                          cursor: "pointer",
                          fontSize: "11px",
                        }}
                      >
                        Info
                      </button>
                    </FlexRow>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {showAddressView ? (
        <div style={{ marginTop: 16 }}>
          <FlexRow
            gap={8}
            style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}
          >
            <h4
              style={{
                fontSize: 13,
                color: "#94a3b8",
                margin: 0,
                fontWeight: 600,
              }}
            >
              Bank Wallet · Conti
            </h4>
            <BackButton onClick={onBack}>Torna al grafico</BackButton>
          </FlexRow>
          <TableWrap>
            <SmallTable>
              <thead>
                <tr>
                  <SmallTh>Currency</SmallTh>
                  <SmallTh>Label</SmallTh>
                  <SmallTh>IBAN / Account</SmallTh>
                  <SmallTh>Intestatario</SmallTh>
                  <SmallTh>Network</SmallTh>
                  <SmallTh>Azioni</SmallTh>
                </tr>
              </thead>
              <tbody>
                {addresses && addresses.length > 0 ? (
                  addresses.map((addr, idx) => (
                    <tr key={`bank-addr-${idx}`}>
                      <SmallTd>{addr.currency}</SmallTd>
                      <SmallTd>{addr.label}</SmallTd>
                      <SmallTd style={{ fontFamily: "monospace", fontSize: "11px" }}>
                        {addr.iban}
                      </SmallTd>
                      <SmallTd>{addr.holder}</SmallTd>
                      <SmallTd>
                        <NetworkBadge>{addr.network}</NetworkBadge>
                      </SmallTd>
                      <SmallTd>
                        <button
                          style={{
                            fontSize: 11,
                            borderRadius: 999,
                            border: "1px solid #1f2937",
                            padding: "2px 8px",
                            background: "#020617",
                            color: "#e5e7eb",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => {
                            navigator.clipboard?.writeText(addr.iban);
                            toast.success("IBAN copied to clipboard");
                          }}
                        >
                          Copy
                        </button>
                      </SmallTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <SmallTd colSpan={6} style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
                      No bank accounts available
                    </SmallTd>
                  </tr>
                )}
              </tbody>
            </SmallTable>
          </TableWrap>
        </div>
      ) : activity && activity.wallet === "Bank" ? (
        <div style={{ marginTop: 16 }}>
          <FlexRow
            gap={8}
            style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}
          >
            <h4
              style={{
                fontSize: 13,
                color: "#94a3b8",
                margin: 0,
                fontWeight: 600,
              }}
            >
              Attività · Bank · {activity.symbol}
            </h4>
            <BackButton onClick={() => onBackFromActivity && onBackFromActivity("Bank")}>Torna al grafico</BackButton>
          </FlexRow>
          <TableWrap>
            <SmallTable>
              <thead>
                <tr>
                  <SmallTh>Direzione</SmallTh>
                  <SmallTh>Importo</SmallTh>
                  <SmallTh>Descrizione</SmallTh>
                  <SmallTh>Ref/Hash</SmallTh>
                  <SmallTh>Data/Ora</SmallTh>
                </tr>
              </thead>
              <tbody>
                {isLoadingActivities ? (
                  <tr>
                    <SmallTd colSpan={5} style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
                      Loading activities...
                    </SmallTd>
                  </tr>
                ) : movements && movements.length > 0 ? (
                  movements
                    .filter((m) => m.wallet === "Bank" && m.symbol === activity.symbol)
                    .map((m, idx) => (
                      <tr key={`bank-${activity.symbol}-movement-${idx}`}>
                        <SmallTd>
                          <BadgeDir direction={m.direction}>
                            {m.direction === "in" ? "Entrata" : "Uscita"}
                          </BadgeDir>
                        </SmallTd>
                        <SmallTd>
                          <FormattedNumber value={m.amount || 0} maxDecimals={2} /> {m.symbol}
                        </SmallTd>
                        <SmallTd>{m.desc || "Movimento bancario"}</SmallTd>
                        <SmallTd style={{ fontFamily: "monospace", fontSize: "11px" }}>
                          {m.hash || m.ref || "-"}
                        </SmallTd>
                        <SmallTd>{m.ts}</SmallTd>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <SmallTd colSpan={5} style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
                      No activities available for {activity.symbol}
                    </SmallTd>
                  </tr>
                )}
              </tbody>
            </SmallTable>
          </TableWrap>
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 6px", fontWeight: 600 }}>
            Bank Wallet · Currency Treemap
          </h4>
          <TreemapBar>
            {items.map((a) => {
              const value = a.balance <= 0 ? 0.0001 : a.balance;
              const width = (value / totalForTreemap) * 100;
              return (
                <TreemapSegment key={`bank-${a.currency}-seg`} width={width}>
                  <TreemapLabel>
                    {a.currency} · <FormattedNumber value={a.balance || 0} symbol="$" maxDecimals={2} />
                  </TreemapLabel>
                </TreemapSegment>
              );
            })}
          </TreemapBar>
        </div>
      )}

      {/* INFO TOKEN VIEW */}
      {infoToken && (
        <div style={{ marginTop: "16px", padding: "16px", background: "#0b1220", borderRadius: "8px", border: "1px solid #1f2937" }}>
          <h4 style={{ margin: "0 0 12px", fontSize: "14px", color: "#e5e7eb" }}>Token Info: {infoToken.currency}</h4>
          <div style={{ fontSize: "12px", color: "#94a3b8", lineHeight: "1.6" }}>
            <div><strong>Currency:</strong> {infoToken.currency}</div>
            <div><strong>Quantity:</strong> <FormattedNumber value={infoToken.qty || 0} maxDecimals={8} /></div>
            <div><strong>Balance:</strong> <FormattedNumber value={infoToken.balance || 0} symbol="$" maxDecimals={2} /></div>
          </div>
          <button
            onClick={() => setInfoToken(null)}
            style={{
              marginTop: "12px",
              padding: "6px 10px",
              borderRadius: "6px",
              background: "#00f7d2",
              border: "none",
              cursor: "pointer",
              color: "#001018",
              fontWeight: 600,
            }}
          >
            Close Info
          </button>
        </div>
      )}
    </Card>

      {/* WITHDRAW SIDEBAR - Rendered outside Card for proper positioning */}
      {sidebarWithdraw && (
        <>
          <SidebarOverlay onClick={() => setSidebarWithdraw(null)} />
          <SidebarGlass>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>Withdraw {sidebarWithdraw.currency}</h3>
              <button
                onClick={() => {
                  setSidebarWithdraw(null);
                  setBankWithdrawAmount("");
                  setBankWithdrawCoin(null);
                  setBankWithdrawReceiver(null);
                  setChallengeId("");
                  setOtpCode("");
                  setSelectedBankWalletFrom("intraledger_payment");
                  setSelectedBankWalletTo("virtual");
                }}
                style={{
                  background: "none",
                  border: "1px solid #5ce9ff",
                  color: "#5ce9ff",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            {/* FROM */}
            <div style={{ marginTop: "16px" }}>From</div>
            <select
              value={selectedBankWalletFrom}
              onChange={(e) => {
                setSelectedBankWalletFrom(e.target.value);
                setBankWithdrawCoin(null);
                setBankWithdrawAmount("");
                setBankWithdrawReceiver(null);
                setBankApproverData([]);
              }}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "8px",
                marginTop: "4px",
                background: "#0b1220",
                color: "#e5e7eb",
                border: "1px solid #1f2937",
              }}
            >
              <option value="intraledger_payment">Intraledger Payment</option>
              <option value="sepa_payment">Sepa Payment (Only EUR)</option>
              <option value="onChain_Transection">Crypto Asset</option>
            </select>

            {/* TO - Only show for Crypto Asset */}
            {selectedBankWalletFrom === "onChain_Transection" && (
              <>
                <div style={{ marginTop: "16px" }}>To</div>
                <select
                  value={selectedBankWalletTo}
                  onChange={(e) => {
                    setSelectedBankWalletTo(e.target.value);
                    setBankWithdrawReceiver(null);
                    setBankApproverData([]);
                    setOnChainWalletAddress("");
                  }}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "8px",
                    marginTop: "4px",
                    background: "#0b1220",
                    color: "#e5e7eb",
                    border: "1px solid #1f2937",
                  }}
                >
                  <option value="onChain">Onchain Wallet</option>
                  <option value="virtual">Virtual Wallet</option>
                  <option value="external_wallet">External</option>
                </select>
              </>
            )}

            {/* COIN */}
            <div style={{ marginTop: "16px" }}>
              Coin <span style={{ color: "#ff3b6b" }}>*</span>
            </div>
            {isLoading ? (
              <div style={{ marginTop: "8px", color: "#94a3b8", fontSize: "12px" }}>Loading coins...</div>
            ) : (
              <select
                value={bankWithdrawCoin?.value || ""}
                onChange={(e) => {
                  const selected = bankWithdrawOptions.find(opt => opt.value === e.target.value);
                  setBankWithdrawCoin(selected);
                  setBankWithdrawAmount("");
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "8px",
                  marginTop: "4px",
                  background: "#0b1220",
                  color: "#e5e7eb",
                  border: "1px solid #1f2937",
                }}
              >
                <option value="">Select coin...</option>
                {bankWithdrawOptions.map((coin) => (
                  <option key={coin.value} value={coin.value}>
                    {coin.value} ({coin.hAmount || coin.availableBalance || 0})
                  </option>
                ))}
              </select>
            )}

            {/* BALANCE */}
            {bankWithdrawCoin && (
              <div style={{ marginTop: "12px", fontSize: "12px", color: "#94a3b8" }}>
                Balance: {bankWithdrawCoin.hAmount || bankWithdrawCoin.availableBalance || 0} {bankWithdrawCoin.value}
              </div>
            )}

            {/* APPROX AMOUNT AFTER FEE - Only for onChain transactions */}
            {selectedBankWalletFrom === "onChain_Transection" && bankWithdrawAmount && onchainFeeEstimation && (
              <div style={{ marginTop: "12px", fontSize: "12px", color: "#94a3b8" }}>
                Approx Amount after Fee: <FormattedNumber value={approxAmountAfterFee} maxDecimals={5} /> {bankWithdrawCoin?.value}
              </div>
            )}

            {/* RECIPIENT ADDRESS */}
            {selectedBankWalletFrom !== "sepa_payment" && (
              <>
                <div style={{ marginTop: "16px" }}>
                  Recipient Address <span style={{ color: "#ff3b6b" }}>*</span>
                </div>
                {selectedBankWalletTo === "external_wallet" && selectedBankWalletFrom === "onChain_Transection" ? (
                  <input
                    value={onChainWalletAddress}
                    onChange={(e) => {
                      setOnChainWalletAddress(e.target.value);
                      // Reset fee when address changes
                      setOnchainFeeEstimation(null);
                      setApproxAmountAfterFee(0);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      marginTop: "4px",
                      borderRadius: "8px",
                      border: "1px solid #1f2937",
                      background: "#0b1220",
                      color: "#e5e7eb",
                    }}
                    placeholder="Enter destination address"
                  />
                ) : (
                  <div style={{ position: "relative" }}>
                    <input
                      value={bankWithdrawReceiver?.value || (selectedBankWalletFrom === "onChain_Transection" && selectedBankWalletTo === "virtual" && strigaWalletAddress?.blockchainDepositAddress ? "Your Virtual Wallet" : "") || ""}
                      onChange={(e) => {
                        if (e.target.value.length >= 2) {
                          handleBankRecipientSearch(e.target.value);
                        } else {
                          setBankApproverData([]);
                          setBankWithdrawReceiver(null);
                        }
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        marginTop: "4px",
                        borderRadius: "8px",
                        border: "1px solid #1f2937",
                        background: "#0b1220",
                        color: "#e5e7eb",
                      }}
                      placeholder={searchLoad ? "Searching..." : "Search recipient by name"}
                    />
                    {bankApproverData.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          marginTop: "4px",
                          background: "#0b1220",
                          border: "1px solid #1f2937",
                          borderRadius: "8px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          zIndex: 1000,
                        }}
                      >
                        {bankApproverData.map((option) => (
                          <div
                            key={option.id}
                            onClick={() => {
                              setBankWithdrawReceiver(option);
                              setDefaultBenificiary(option.destinationUserId || option.striga_user_id);
                              setBankApproverData([]);
                              if (selectedBankWalletFrom === "intraledger_payment" && !option.striga_user_id) {
                                toast.error("This user don't have striga_user_id");
                              } else if (selectedBankWalletFrom === "onChain_Transection" && selectedBankWalletTo === "onChain") {
                                const chainId = bankWithdrawCoin?.chainId;
                                const hasOnChainAddress = 
                                  (chainId === REACT_APP_SOLANA && (option.onChainSolAddress || option.sol_address)) ||
                                  (chainId === REACT_APP_TRON && (option.trcWalletAddress || option.address)) ||
                                  (chainId !== REACT_APP_SOLANA && chainId !== REACT_APP_TRON && (option.onChainAddress || option.secondary_wallet_address));
                                if (!hasOnChainAddress) {
                                  toast.error("This user don't have onChain Wallet for this network");
                                }
                              }
                              // Address will be updated by useEffect based on network
                            }}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              borderBottom: "1px solid #1f2937",
                              color: "#e5e7eb",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "#1f2937";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "transparent";
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* BIC and IBAN for SEPA */}
            {selectedBankWalletFrom === "sepa_payment" && (
              <>
                <div style={{ marginTop: "16px" }}>
                  BIC <span style={{ color: "#ff3b6b" }}>*</span>
                </div>
                <input
                  value={bic || strigaWalletAddress?.BIC || ""}
                  onChange={(e) => setBic(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                    background: "#0b1220",
                    color: "#e5e7eb",
                  }}
                  placeholder="Enter BIC"
                />
                <div style={{ marginTop: "16px" }}>
                  IBAN <span style={{ color: "#ff3b6b" }}>*</span>
                </div>
                <input
                  value={iban || strigaWalletAddress?.IBAN || ""}
                  onChange={(e) => setIban(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                    background: "#0b1220",
                    color: "#e5e7eb",
                  }}
                  placeholder="Enter IBAN"
                />
              </>
            )}

            {/* AMOUNT */}
            <div style={{ marginTop: "16px" }}>
              Amount <span style={{ color: "#ff3b6b" }}>*</span>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="number"
                value={bankWithdrawAmount}
                onChange={(e) => setBankWithdrawAmount(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #1f2937",
                  background: "#0b1220",
                  color: "#e5e7eb",
                }}
                placeholder="Enter amount"
              />
              {bankWithdrawCoin && (
                <button
                  onClick={() => setBankWithdrawAmount(String(bankWithdrawCoin.hAmount || bankWithdrawCoin.availableBalance || 0))}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    background: "#1f2937",
                    border: "1px solid #5ce9ff",
                    color: "#5ce9ff",
                    cursor: "pointer",
                  }}
                >
                  MAX
                </button>
              )}
            </div>

            {/* OTP CODE */}
            {challengeId && (
              <>
                <div style={{ marginTop: "16px" }}>OTP Code <span style={{ color: "#ff3b6b" }}>*</span></div>
                <input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    marginTop: "4px",
                    borderRadius: "8px",
                    border: "1px solid #1f2937",
                    background: "#0b1220",
                    color: "#e5e7eb",
                  }}
                  placeholder="Enter OTP code"
                />
              </>
            )}

            {/* WARNING */}
            <div style={{ marginTop: "16px", padding: "12px", background: "rgba(255, 193, 7, 0.1)", borderRadius: "8px", border: "1px solid rgba(255, 193, 7, 0.3)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#ffc107" }}>
                <span>⚠</span>
                <span>Please double-check the destination address and selected network before confirming the withdrawal.</span>
              </div>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              onClick={challengeId ? handleBankOTPVerify : handleBankWithdrawSubmit}
              disabled={isLoading || !bankWithdrawAmount || !bankWithdrawCoin}
              style={{
                width: "100%",
                marginTop: "16px",
                padding: "12px",
                borderRadius: "8px",
                background: (isLoading || !bankWithdrawAmount || !bankWithdrawCoin) ? "#1f2937" : "#00f7d2",
                border: "none",
                color: (isLoading || !bankWithdrawAmount || !bankWithdrawCoin) ? "#94a3b8" : "#001018",
                cursor: (isLoading || !bankWithdrawAmount || !bankWithdrawCoin) ? "not-allowed" : "pointer",
                fontWeight: 600,
                opacity: (isLoading || !bankWithdrawAmount || !bankWithdrawCoin) ? 0.5 : 1,
              }}
            >
              {isLoading ? "Processing..." : challengeId ? "Verify OTP" : "Send Withdrawal"}
            </button>
          </SidebarGlass>
        </>
      )}
    </>
  );
}

export default BankWalletBlock;

